import { FC, useEffect, useRef, useState } from 'react'
import { Form, Checkbox, ButtonLoader } from '@wellesley/ui'
import { Info, LockKeyhole, EarthIcon } from 'lucide-react'

import CommonFormField from 'components/form/CommonFormField'
import SlugField from 'components/form/SlugField'
import SelectWithRadio from 'components/form/SelectWithRadio'
import SwitchWithLabel from 'components/SwitchWithLabel'
import CustomTooltip from 'components/CustomTooltip'
import useDebounceValue from 'hooks/useDebounceValue'

import {
  ChannelFormProps,
  ChannelPrivacyOptions,
} from '../../types/channel-form'
import { useChannelForm } from '../../hooks/useChannelForm'
import { staticState } from '@wellesley/host-remote/common/staticState'
import {
  handleChannelNameChange,
  updateGlobalUrlFromSlug,
} from '../../helpers/form-utils'
import { getAvailablePrivacyOptions } from '../../helpers/privacy'
import { isPrivateGroup } from '../../../../helpers/groupSettings'
import {
  channelNameMaxLength,
  channelSlugMaxLength,
  channelSlugValidation,
} from '@wellesley/forms'

const PRIVACY_OPTION_ICONS = {
  [ChannelPrivacyOptions.public]: <EarthIcon />,
  [ChannelPrivacyOptions.private]: <LockKeyhole />,
}

const ChannelForm: FC<ChannelFormProps> = ({
  values,
  disabled,
  group,
  handleCancel,
  handleSubmit,
  renderTopPanel,
  onDirtyChange,
}) => {
  const { groupId, privacy: groupPrivacy, groupName } = group
  const isPrivateGroupValue = isPrivateGroup(group)
  const {
    form,
    onSubmit,
    handlePrivacyChange,
    handleAddGlobalUrlChange,
    submitDisabled,
    canAddGlobalUrl,
    isLoading,
  } = useChannelForm({
    values,
    groupId,
    groupPrivacy,
    handleCancel,
    handleSubmit,
  })

  const addGlobalUrlValue = form.watch('addGlobalUrl')
  const autoSubscribeValue = form.watch('autoSubscribe')
  const channelName = form.watch('channelName')
  const channelSlug = form.watch('channelSlug')

  const baseUrl = staticState.get().baseUrl || window.location.origin
  const channelSlugPrefix = `${baseUrl}/g/${groupName}/@`

  const isDirty = form.formState.isDirty
  const debouncedChannelName = useDebounceValue(channelName, 500)
  const debouncedChannelSlug = useDebounceValue(channelSlug, 500)
  const manualSlugChangeRef = useRef(Boolean(values?.channelSlug))
  const manualGlobalUrlChangeRef = useRef(Boolean(values?.globalUrl))
  const [isSubmitValidating, setIsSubmitValidating] = useState(false)

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    manualSlugChangeRef.current = Boolean(values?.channelSlug)
  }, [values?.channelSlug])

  useEffect(() => {
    if (!debouncedChannelName?.trim()) {
      return
    }

    const syncChannelNameChanges = async () => {
      if (!manualSlugChangeRef.current) {
        await handleChannelNameChange(form, groupId)
      }

      if (!addGlobalUrlValue || manualGlobalUrlChangeRef.current) {
        return
      }

      const { channelSlug, channelName } = form.getValues()
      const source = channelSlug?.trim() ? channelSlug : channelName

      if (source?.trim()) {
        await updateGlobalUrlFromSlug(form, source)
      }
    }

    void syncChannelNameChanges()
  }, [addGlobalUrlValue, debouncedChannelName, form, groupId])

  useEffect(() => {
    manualGlobalUrlChangeRef.current = Boolean(values?.globalUrl)
  }, [values?.globalUrl])

  useEffect(() => {
    if (
      !addGlobalUrlValue ||
      !debouncedChannelSlug?.trim() ||
      manualGlobalUrlChangeRef.current
    ) {
      return
    }

    void updateGlobalUrlFromSlug(form, debouncedChannelSlug)
  }, [addGlobalUrlValue, debouncedChannelSlug, form])

  const handleChannelNameBlur = () => {
    const channelName = form.getValues('channelName')
    if (channelName) {
      void handleChannelNameChange(form, groupId)
    }
  }

  const validateSlugCharacters = (fieldName: 'channelSlug' | 'globalUrl') => {
    const value = form.getValues(fieldName)

    if (!value) {
      form.clearErrors(fieldName)
      return
    }

    const result = channelSlugValidation.safeParse(value)
    if (result.success) {
      form.clearErrors(fieldName)
      return
    }

    const relevantIssue = result.error.errors.find((issue) => {
      if (issue.code === 'invalid_string' && issue.validation === 'regex') {
        return true
      }

      return issue.code === 'too_small'
    })

    if (relevantIssue) {
      form.setError(fieldName, {
        type: 'manual',
        message: relevantIssue.message,
      })
      return
    }

    form.clearErrors(fieldName)
  }

  const handleChannelNameFieldChange = () => {
    form.clearErrors('channelName')
  }

  const handleChannelSlugChange = () => {
    if (!manualSlugChangeRef.current) {
      manualSlugChangeRef.current = true
    }
    validateSlugCharacters('channelSlug')
  }

  const handleGlobalUrlChange = () => {
    if (!manualGlobalUrlChangeRef.current) {
      manualGlobalUrlChangeRef.current = true
    }

    validateSlugCharacters('globalUrl')
  }

  const handleAddGlobalUrlChangeWrapper = (checked: boolean) => {
    if (checked) {
      manualGlobalUrlChangeRef.current = false
    }
    handleAddGlobalUrlChange(checked)
  }

  const availablePrivacyOptions = getAvailablePrivacyOptions(groupPrivacy).map(
    (option) => ({
      ...option,
      icon: PRIVACY_OPTION_ICONS[option.key],
    }),
  )

  const handleSubmitWithValidationState = form.handleSubmit(
    async (data) => {
      setIsSubmitValidating(false)
      await onSubmit(data)
    },
    () => {
      setIsSubmitValidating(false)
    },
  )

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setIsSubmitValidating(true)
    void handleSubmitWithValidationState(event)
  }

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit}>
        {renderTopPanel?.({ disabled: submitDisabled })}

        <div className="flex max-w-[600px] flex-col gap-m">
          <CommonFormField
            name="channelName"
            control={form.control}
            displayName="Channel name"
            maxChars={channelNameMaxLength}
            disabled={disabled}
            onBlur={handleChannelNameBlur}
            onFieldChange={handleChannelNameFieldChange}
          />

          <SelectWithRadio
            form={form}
            name="privacy"
            title={`Privacy ${isPrivateGroupValue ? '(private groups support private channels only)' : ''}`}
            options={availablePrivacyOptions}
            disabled={isPrivateGroupValue}
            onChange={handlePrivacyChange}
          />

          <SlugField
            name="channelSlug"
            control={form.control}
            displayName="Channel slug"
            maxChars={channelSlugMaxLength}
            disabled={disabled}
            prefix={channelSlugPrefix}
            onFieldChange={handleChannelSlugChange}
          />

          {canAddGlobalUrl && (
            <div className="flex flex-col gap-xxs">
              <div className="flex items-center gap-xxs">
                <Checkbox
                  checked={addGlobalUrlValue}
                  onCheckedChange={handleAddGlobalUrlChangeWrapper}
                  disabled={disabled}
                />
                <label className="text-base font-medium text-gray-700">
                  Add global URL
                </label>
                <CustomTooltip
                  tooltipText={
                    <p>
                      A global URL is a direct link to a channel that works
                      <br />
                      independently of the group&apos;s address or name
                    </p>
                  }
                  contentProps={{
                    side: 'bottom',
                    align: 'start',
                  }}
                >
                  <Info size={20} className="text-gray-900" />
                </CustomTooltip>
              </div>

              {addGlobalUrlValue && (
                <SlugField
                  name="globalUrl"
                  control={form.control}
                  maxChars={channelSlugMaxLength}
                  className="mt-xxs"
                  disabled={disabled}
                  prefix={`${baseUrl}/@`}
                  onFieldChange={handleGlobalUrlChange}
                />
              )}
            </div>
          )}

          <div className="flex flex-col rounded-lg border border-gray-200 p-m">
            <SwitchWithLabel
              checked={autoSubscribeValue}
              onChange={(checked) => form.setValue('autoSubscribe', checked)}
              leftLabelText="Auto-subscribe"
              disabled={disabled}
              className="flex-row justify-between md:w-full  md:flex-row md:justify-between"
            />
            <p className="text-base text-gray-500">
              Automatic subscription for new group members on join
            </p>
          </div>
        </div>

        <div className="relative flex justify-end pt-m">
          <ButtonLoader
            data-testid="create-channel-button"
            size="m"
            variant="primary"
            type="submit"
            disabled={
              submitDisabled || disabled || isLoading || isSubmitValidating
            }
            isLoading={isLoading || isSubmitValidating}
          >
            Create channel
          </ButtonLoader>
        </div>
      </form>
    </Form>
  )
}

export default ChannelForm
